/**
 * Section schema for the CMS.
 * Shared between the admin editor (producer) and the storefront renderer (consumer).
 */

export type HeroSection = {
  type: 'hero';
  id: string;
  /** Background image URL, full-width hero. */
  imageUrl?: string;
  /** Optional darken overlay percentage (0-100) for text legibility. */
  overlayPercent?: number;
  headline: string;
  subheadline?: string;
  ctaLabel?: string;
  ctaHref?: string;
  /** vertical padding intensity */
  height?: 'sm' | 'md' | 'lg';
  /** text alignment */
  align?: 'left' | 'center' | 'right';
};

export type RichTextSection = {
  type: 'rich-text';
  id: string;
  /** HTML content (already-sanitized markdown render or raw HTML). */
  html: string;
  /** Container width preset. */
  width?: 'narrow' | 'normal' | 'wide';
  align?: 'left' | 'center';
};

export type ImageWithTextSection = {
  type: 'image-with-text';
  id: string;
  imageUrl: string;
  imageAlt?: string;
  imagePosition?: 'left' | 'right';
  headline?: string;
  body?: string;
  ctaLabel?: string;
  ctaHref?: string;
};

export type CollectionGridSection = {
  type: 'collection-grid';
  id: string;
  /** Collection.id (cuid) */
  collectionId: string;
  /** Override the section title; falls back to collection title. */
  title?: string;
  showTitle?: boolean;
  productLimit?: number;
  /** Layout shape */
  displayType?: 'CAROUSEL' | 'GRID' | 'LIST';
};

/** Compact promotional banner, tighter than Hero, often mid-page. */
export type BannerSection = {
  type: 'banner';
  id: string;
  text: string;
  ctaLabel?: string;
  ctaHref?: string;
  /** Tailwind background color, e.g. 'bg-emerald-600'. */
  background?: string;
  /** Tailwind text color: defaults to white. */
  textColor?: string;
};

/** Vertical whitespace between sections. */
export type SpacerSection = {
  type: 'spacer';
  id: string;
  /** rem of vertical space (1–12). */
  size?: number;
};

/** Embedded video (YouTube / Vimeo URL). */
export type VideoSection = {
  type: 'video';
  id: string;
  url: string;
  aspect?: '16/9' | '4/3' | '1/1';
  caption?: string;
};

/** Accordion-style FAQ list. */
export type FaqSection = {
  type: 'faq';
  id: string;
  heading?: string;
  items: { q: string; a: string }[];
};

/** Email-capture form. Submission posts to /api/email-leads. */
export type EmailCaptureSection = {
  type: 'email-capture';
  id: string;
  heading?: string;
  description?: string;
  buttonLabel?: string;
  /** Tag attached to the lead. */
  source?: string;
};

/** Product carousel by explicit SKU list or Collection. */
export type ProductCarouselSection = {
  type: 'product-carousel';
  id: string;
  title?: string;
  collectionId?: string;
  productLimit?: number;
};

export type SectionConfig =
  | HeroSection
  | RichTextSection
  | ImageWithTextSection
  | CollectionGridSection
  | BannerSection
  | SpacerSection
  | VideoSection
  | FaqSection
  | EmailCaptureSection
  | ProductCarouselSection;

export type SectionType = SectionConfig['type'];

export const SECTION_TYPES: SectionType[] = [
  'hero',
  'banner',
  'rich-text',
  'image-with-text',
  'collection-grid',
  'product-carousel',
  'video',
  'faq',
  'email-capture',
  'spacer',
];

export const SECTION_LABELS: Record<SectionType, string> = {
  hero: 'Hero',
  banner: 'Banner',
  'rich-text': 'Rich text',
  'image-with-text': 'Image with text',
  'collection-grid': 'Collection grid',
  'product-carousel': 'Product carousel',
  video: 'Video',
  faq: 'FAQ accordion',
  'email-capture': 'Email capture',
  spacer: 'Spacer',
};

/** Lightweight runtime validator. We don't pull zod here to keep client bundle small. */
export function isValidSection(value: unknown): value is SectionConfig {
  if (!value || typeof value !== 'object') return false;
  const v = value as { type?: unknown; id?: unknown };
  if (typeof v.id !== 'string' || !v.id) return false;
  if (typeof v.type !== 'string') return false;
  if (!SECTION_TYPES.includes(v.type as SectionType)) return false;
  return true;
}

export function parseSections(raw: unknown): SectionConfig[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isValidSection);
}

/** Produce a fresh blank section of the given type with a unique id. */
export function blankSection(type: SectionType): SectionConfig {
  const id = `${type}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
  switch (type) {
    case 'hero':
      return { type: 'hero', id, headline: 'Headline', height: 'md', align: 'center' };
    case 'rich-text':
      return { type: 'rich-text', id, html: '<p>Start writing…</p>', width: 'normal', align: 'left' };
    case 'image-with-text':
      return {
        type: 'image-with-text',
        id,
        imageUrl: '',
        imagePosition: 'left',
        headline: 'Headline',
        body: 'Add supporting copy here.',
      };
    case 'collection-grid':
      return {
        type: 'collection-grid',
        id,
        collectionId: '',
        showTitle: true,
        productLimit: 12,
        displayType: 'GRID',
      };
    case 'banner':
      return { type: 'banner', id, text: 'Free same-day delivery', background: 'bg-emerald-600', textColor: 'text-white' };
    case 'spacer':
      return { type: 'spacer', id, size: 4 };
    case 'video':
      return { type: 'video', id, url: '', aspect: '16/9' };
    case 'faq':
      return {
        type: 'faq',
        id,
        heading: 'Frequently asked questions',
        items: [
          { q: 'How fast is delivery?', a: 'Same-day for orders before 8pm.' },
        ],
      };
    case 'email-capture':
      return {
        type: 'email-capture',
        id,
        heading: 'Stay in the loop',
        description: 'Drop your email for new arrivals and deals.',
        buttonLabel: 'Subscribe',
        source: 'page',
      };
    case 'product-carousel':
      return { type: 'product-carousel', id, title: '', collectionId: '', productLimit: 12 };
  }
}
